import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { Loader2, ExternalLink, CheckCircle2, XCircle, PauseCircle, PlayCircle } from "lucide-react";

type AppRow = {
  id: string;
  full_name: string;
  email: string;
  brand: string | null;
  website: string | null;
  handle: string | null;
  pitch: string;
  status: "new" | "reviewing" | "approved" | "rejected" | "archived";
  review_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
};

type VendorRow = {
  id: string;
  user_id: string;
  partner_application_id: string | null;
  display_name: string;
  slug: string;
  contact_email: string | null;
  status: "pending_onboarding" | "onboarding" | "active" | "paused" | "suspended" | "rejected";
  stripe_charges_enabled: boolean;
  stripe_payouts_enabled: boolean;
  identity_verified: boolean;
  approved_at: string | null;
  suspended_at: string | null;
  suspended_reason: string | null;
  created_at: string;
};

const appStatusVariant: Record<AppRow["status"], string> = {
  new: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  reviewing: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  rejected: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  archived: "bg-muted text-muted-foreground",
};

const vendorStatusVariant: Record<VendorRow["status"], string> = {
  pending_onboarding: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  onboarding: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  paused: "bg-muted text-muted-foreground",
  suspended: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  rejected: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
};

export default function StoreVendorApprovalPanel() {
  const [applications, setApplications] = useState<AppRow[]>([]);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    const [{ data: apps, error: appErr }, { data: vens, error: venErr }] = await Promise.all([
      supabase
        .from("partner_applications")
        .select("id, full_name, email, brand, website, handle, pitch, status, review_notes, reviewed_at, created_at")
        .eq("partner_type", "vendor")
        .order("created_at", { ascending: false })
        .limit(200),
      supabase
        .from("store_vendors")
        .select("id, user_id, partner_application_id, display_name, slug, contact_email, status, stripe_charges_enabled, stripe_payouts_enabled, identity_verified, approved_at, suspended_at, suspended_reason, created_at")
        .order("created_at", { ascending: false })
        .limit(200),
    ]);
    if (appErr) toast({ title: "Failed to load applications", description: appErr.message, variant: "destructive" });
    if (venErr) toast({ title: "Failed to load vendors", description: venErr.message, variant: "destructive" });
    setApplications((apps as AppRow[]) ?? []);
    setVendors((vens as VendorRow[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const vendorByApp = useMemo(() => {
    const m = new Map<string, VendorRow>();
    vendors.forEach((v) => v.partner_application_id && m.set(v.partner_application_id, v));
    return m;
  }, [vendors]);

  const setAppStatus = async (id: string, status: AppRow["status"]) => {
    setBusyId(id);
    const { data: u } = await supabase.auth.getUser();
    const review_notes = notes[id]?.trim() || null;
    const { error } = await supabase
      .from("partner_applications")
      .update({
        status,
        review_notes,
        reviewer_id: u.user?.id ?? null,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", id);
    setBusyId(null);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: `Application ${status}` });
    load();
  };

  const setVendorStatus = async (
    id: string,
    status: VendorRow["status"],
    reason?: string,
  ) => {
    setBusyId(id);
    const patch: {
      status: VendorRow["status"];
      approved_at?: string | null;
      suspended_at?: string | null;
      suspended_reason?: string | null;
    } = { status };
    if (status === "active") {
      patch.approved_at = new Date().toISOString();
      patch.suspended_at = null;
      patch.suspended_reason = null;
    }
    if (status === "suspended" || status === "paused") {
      patch.suspended_at = new Date().toISOString();
      patch.suspended_reason = reason ?? null;
    }
    const { error } = await supabase.from("store_vendors").update(patch).eq("id", id);
    setBusyId(null);
    if (error) {
      toast({ title: "Vendor update failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: `Vendor ${status.replace("_", " ")}` });
    load();
  };

  const pendingApps = applications.filter((a) => a.status === "new" || a.status === "reviewing");
  const decidedApps = applications.filter((a) => a.status === "approved" || a.status === "rejected" || a.status === "archived");
  const pendingVendors = vendors.filter((v) => v.status === "pending_onboarding" || v.status === "onboarding");
  const activeVendors = vendors.filter((v) => v.status === "active" || v.status === "paused" || v.status === "suspended");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading vendor queue…
      </div>
    );
  }

  return (
    <Tabs defaultValue="applications" className="space-y-4">
      <div className="flex items-center justify-between">
        <TabsList>
          <TabsTrigger value="applications">
            Applications {pendingApps.length > 0 && <Badge className="ml-2" variant="secondary">{pendingApps.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="onboarding">
            Onboarding {pendingVendors.length > 0 && <Badge className="ml-2" variant="secondary">{pendingVendors.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="active">Active vendors</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <Button variant="outline" size="sm" onClick={load}>Refresh</Button>
      </div>

      <TabsContent value="applications" className="space-y-3">
        {pendingApps.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending vendor applications.</p>
        ) : (
          pendingApps.map((a) => {
            const v = vendorByApp.get(a.id);
            return (
              <Card key={a.id} className="p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{a.brand || a.full_name}</div>
                    <div className="text-sm text-muted-foreground">
                      {a.full_name} · <a href={`mailto:${a.email}`} className="underline">{a.email}</a>
                      {a.handle && <> · @{a.handle}</>}
                    </div>
                    {a.website && (
                      <a
                        href={a.website.startsWith("http") ? a.website : `https://${a.website}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        {a.website} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={appStatusVariant[a.status]}>{a.status}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap">{a.pitch}</p>
                {v && (
                  <div className="text-xs text-muted-foreground">
                    Vendor record exists: <code>{v.slug}</code> ({v.status})
                  </div>
                )}
                <Textarea
                  placeholder="Internal review notes (optional, visible to admins only)"
                  value={notes[a.id] ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [a.id]: e.target.value }))}
                  rows={2}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => setAppStatus(a.id, "approved")}
                    disabled={busyId === a.id}
                  >
                    <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAppStatus(a.id, "reviewing")}
                    disabled={busyId === a.id || a.status === "reviewing"}
                  >
                    Mark reviewing
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setAppStatus(a.id, "rejected")}
                    disabled={busyId === a.id}
                  >
                    <XCircle className="mr-1 h-4 w-4" /> Reject
                  </Button>
                </div>
              </Card>
            );
          })
        )}
      </TabsContent>

      <TabsContent value="onboarding" className="space-y-3">
        {pendingVendors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No vendors awaiting publish approval.</p>
        ) : (
          pendingVendors.map((v) => (
            <Card key={v.id} className="p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{v.display_name}</div>
                  <div className="text-sm text-muted-foreground">
                    <code>{v.slug}</code>
                    {v.contact_email && <> · {v.contact_email}</>}
                  </div>
                </div>
                <Badge className={vendorStatusVariant[v.status]}>{v.status.replace("_", " ")}</Badge>
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>Stripe charges: {v.stripe_charges_enabled ? "✅" : "❌"}</span>
                <span>Stripe payouts: {v.stripe_payouts_enabled ? "✅" : "❌"}</span>
                <span>Identity verified: {v.identity_verified ? "✅" : "❌"}</span>
              </div>
              {(!v.stripe_charges_enabled || !v.identity_verified) && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Vendor has not completed Stripe Connect onboarding and/or identity verification.
                  Approving now will let them publish before this is finished.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => setVendorStatus(v.id, "active")}
                  disabled={busyId === v.id}
                >
                  <CheckCircle2 className="mr-1 h-4 w-4" /> Approve to publish
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    const r = window.prompt("Reason for rejection?") ?? undefined;
                    setVendorStatus(v.id, "rejected", r ?? undefined);
                  }}
                  disabled={busyId === v.id}
                >
                  <XCircle className="mr-1 h-4 w-4" /> Reject vendor
                </Button>
              </div>
            </Card>
          ))
        )}
      </TabsContent>

      <TabsContent value="active" className="space-y-3">
        {activeVendors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active vendors yet.</p>
        ) : (
          activeVendors.map((v) => (
            <Card key={v.id} className="p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{v.display_name}</div>
                  <div className="text-sm text-muted-foreground">
                    <code>{v.slug}</code>
                    {v.contact_email && <> · {v.contact_email}</>}
                  </div>
                  {v.suspended_reason && (
                    <p className="text-xs text-rose-600 mt-1">Suspended: {v.suspended_reason}</p>
                  )}
                </div>
                <Badge className={vendorStatusVariant[v.status]}>{v.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {v.status === "active" && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const r = window.prompt("Pause reason (visible internally)?") ?? undefined;
                        setVendorStatus(v.id, "paused", r ?? undefined);
                      }}
                      disabled={busyId === v.id}
                    >
                      <PauseCircle className="mr-1 h-4 w-4" /> Pause
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        const r = window.prompt("Suspension reason (required)?");
                        if (!r) return;
                        setVendorStatus(v.id, "suspended", r);
                      }}
                      disabled={busyId === v.id}
                    >
                      Suspend
                    </Button>
                  </>
                )}
                {(v.status === "paused" || v.status === "suspended") && (
                  <Button
                    size="sm"
                    onClick={() => setVendorStatus(v.id, "active")}
                    disabled={busyId === v.id}
                  >
                    <PlayCircle className="mr-1 h-4 w-4" /> Reinstate
                  </Button>
                )}
              </div>
            </Card>
          ))
        )}
      </TabsContent>

      <TabsContent value="history" className="space-y-3">
        {decidedApps.length === 0 ? (
          <p className="text-sm text-muted-foreground">No decided applications yet.</p>
        ) : (
          decidedApps.slice(0, 50).map((a) => (
            <Card key={a.id} className="p-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm">
                <span className="font-medium">{a.brand || a.full_name}</span>{" "}
                <span className="text-muted-foreground">· {a.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={appStatusVariant[a.status]}>{a.status}</Badge>
                {a.reviewed_at && (
                  <span className="text-xs text-muted-foreground">
                    {new Date(a.reviewed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </Card>
          ))
        )}
      </TabsContent>
    </Tabs>
  );
}
